import React, { useState, useMemo } from 'react';
import OrdenTrabajoCalendarCard from './OrdenTrabajoCalendarCard';
import { getCalendarDays, groupOrdenesByDate, getDateKey, getMonthName, getWeekDays } from '../../utils/dateHelpers';

/**
 * Calendario mensual de órdenes de trabajo
 * @param {Object} props - Propiedades del componente
 * @param {Array<Object>} props.ordenesTrabajo - Array de objetos orden de trabajo
 * @param {Function} props.onOrdenTrabajoClick - Callback al hacer click en card
 *   Recibe: (ordenTrabajo: Object) => void
 *   El objeto orden de trabajo completo es pasado como parámetro
 * @param {boolean} props.loading - Estado de carga
 * @returns {JSX.Element}
 */
function CalendarioOrdenesTrabajo({ ordenesTrabajo = [], onOrdenTrabajoClick, loading = false }) {
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());

  // Agrupar órdenes de trabajo por fecha
  const ordenesPorFecha = useMemo(
    () => groupOrdenesByDate(ordenesTrabajo),
    [ordenesTrabajo]
  );

  // Obtener días del calendario con órdenes asignadas
  const calendarDays = useMemo(() => {
    const days = getCalendarDays(currentYear, currentMonth);

    days.forEach(day => {
      const key = getDateKey(day.date);
      day.ordenesTrabajo = ordenesPorFecha[key] || [];
    });

    return days;
  }, [currentYear, currentMonth, ordenesPorFecha]);

  /**
   * Navega al mes anterior
   */
  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  /**
   * Navega al mes siguiente
   */
  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  /**
   * Vuelve al mes actual
   */
  const handleToday = () => {
    const now = new Date();
    setCurrentYear(now.getFullYear());
    setCurrentMonth(now.getMonth());
  };

  const weekDays = getWeekDays();

  if (loading) {
    return (
      <div className="calendario-container">
        <div className="text-center" style={{ padding: '3rem' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: 'var(--primary-color)' }}></i>
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Cargando calendario...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="calendario-container">
      {/* Header con navegación */}
      <div className="calendario-header">
        <button
          className="btn btn-secondary btn-sm"
          onClick={handlePrevMonth}
          title="Mes anterior"
        >
          <i className="fas fa-chevron-left"></i>
        </button>

        <h2 className="calendario-title">
          {getMonthName(currentMonth)} {currentYear}
        </h2>

        <button
          className="btn btn-secondary btn-sm"
          onClick={handleNextMonth}
          title="Mes siguiente"
        >
          <i className="fas fa-chevron-right"></i>
        </button>

        <button
          className="btn btn-primary btn-sm"
          onClick={handleToday}
          title="Ir a hoy"
        >
          Hoy
        </button>
      </div>

      {/* Grid de días de la semana */}
      <div className="calendario-grid">
        {weekDays.map(day => (
          <div key={day} className="calendario-weekday">
            {day}
          </div>
        ))}

        {/* Grid de días del mes */}
        {calendarDays.map((day, index) => {
          const hasOrdenes = day.ordenesTrabajo.length > 0;
          const visiblesOrdenes = day.ordenesTrabajo.slice(0, 3);
          const remainingCount = day.ordenesTrabajo.length - 3;

          return (
            <div
              key={index}
              className={`calendario-day
                ${!day.isCurrentMonth ? 'other-month' : ''}
                ${day.isToday ? 'today' : ''}
                ${hasOrdenes ? 'has-ordenes' : ''}
              `}
            >
              <div className="calendario-day-header">
                <span className="calendario-day-number">{day.day}</span>
                {hasOrdenes && (
                  <span className="calendario-day-badge">
                    {day.ordenesTrabajo.length}
                  </span>
                )}
              </div>

              <div className="calendario-day-content">
                {visiblesOrdenes.map(ordenTrabajo => (
                  <OrdenTrabajoCalendarCard
                    key={ordenTrabajo.id}
                    ordenTrabajo={ordenTrabajo}
                    onClick={onOrdenTrabajoClick}
                  />
                ))}

                {remainingCount > 0 && (
                  <div className="calendario-more-badge">
                    +{remainingCount} más
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CalendarioOrdenesTrabajo;
